import {Placement} from '@popperjs/core';
import {some} from 'lodash';
import React, {Component, RefObject} from 'react';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {PopoverProps} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverTypes';
import {isTracker} from '../../../../../../helpers/modelHelpers';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../services/tracking';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InviteDropdownCloseConfirmation} from './inviteDropdownCloseConfirmation';
import {InviteDropdownInviteContainer} from './inviteDropdownInviteContainer';
import {InviteDropdownTrackersContainer} from './inviteDropdownTrackersContainer';

/*
 * Props.
 */

interface InviteDropdownProps {
  userId: number;
  cellsContext: CellsContext;
  conversation: ConversationModel;
  anchor: RefObject<HTMLElement>;
  placement: Placement;
  onRequestClose: PopoverProps['onRequestClose'];
}

/*
 * Component.
 */

interface InviteDropdownState {
  startedWithTrackers: boolean;
  isInviting?: boolean;
  hasInviteComment?: boolean;
  isCloseConfirmationOpen?: boolean;
}

/* eslint react/no-unused-state: 0 */
export class InviteDropdown extends Component<InviteDropdownProps, InviteDropdownState> {
  constructor(props: InviteDropdownProps) {
    super(props);

    // Check if this conversation has any trackers besides us.
    const {userId, conversation} = props;
    const startedWithTrackers = some(conversation.trackers, (t) =>
      Boolean(t && isTracker(t) && t.teammateId !== userId),
    );

    this.state = {startedWithTrackers};
  }

  /*
   * Lifecycle.
   */

  componentDidMount() {
    trackInterfaceEvent(InterfaceEventsEnum.SHARE_MENU, {step: 'open_menu'});
  }

  /*
   * Event handlers.
   */

  private readonly onRequestClose = () => {
    const {onRequestClose} = this.props;
    this.setState(
      ({hasInviteComment}) => {
        // If there's a comment, we need to show a warning
        if (hasInviteComment) {
          return {isCloseConfirmationOpen: true};
        }

        return null;
      },
      () => {
        if (!this.state.hasInviteComment && onRequestClose) {
          onRequestClose();
        }
      },
    );
  };

  private readonly onInviteClick = () => {
    this.setState({
      isInviting: true,
      hasInviteComment: false,
    });

    // Tracking.
    trackInterfaceEvent(InterfaceEventsEnum.SHARE_MENU, {step: 'invite_intent'});
  };

  private readonly onCancelInviteClick = () => {
    const {onRequestClose} = this.props;
    this.setState(
      ({startedWithTrackers}) => {
        // If we had trackers initially, revert back to participants.
        if (startedWithTrackers) {
          return {
            isInviting: false,
            hasInviteComment: false,
          };
        }

        return null;
      },
      () => {
        if (!this.state.startedWithTrackers && onRequestClose) {
          onRequestClose();
        }
      },
    );
  };

  private readonly onHasCommentChange: HandlerOf<boolean> = (hasInviteComment) => {
    this.setState({hasInviteComment});
  };

  private readonly onCloseConfirmationRequestClose = () => {
    this.setState({isCloseConfirmationOpen: false});
  };

  private readonly onCloseConfirmationOkClick = () => {
    const {onRequestClose} = this.props;
    if (!onRequestClose) {
      return;
    }

    onRequestClose();
  };

  /*
   * Render.
   */

  render() {
    return (
      <>
        <Dropdown {...this.props} width={320} onRequestClose={this.onRequestClose}>
          {this.renderContent()}
        </Dropdown>
        {this.maybeRenderCloseConfirmation()}
      </>
    );
  }

  private renderContent() {
    const {isInviting} = this.state;
    const {conversation, cellsContext, anchor, placement} = this.props;

    if (isInviting) {
      return (
        <InviteDropdownInviteContainer
          conversation={conversation}
          cellsContext={cellsContext}
          onCancelClick={this.onCancelInviteClick}
          onRequestClose={this.onRequestClose}
          onHasCommentChange={this.onHasCommentChange}
        />
      );
    }

    return (
      <InviteDropdownTrackersContainer
        cellsContext={cellsContext}
        conversation={conversation}
        onInviteClick={this.onInviteClick}
        onRequestClose={this.onRequestClose}
        anchor={anchor}
        placement={placement}
      />
    );
  }

  private maybeRenderCloseConfirmation() {
    if (!this.state.isCloseConfirmationOpen) {
      return null;
    }

    return (
      <InviteDropdownCloseConfirmation
        onOkClick={this.onCloseConfirmationOkClick}
        onRequestClose={this.onCloseConfirmationRequestClose}
      />
    );
  }
}
